import { useState } from 'react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { SearchInput } from '@/components/ui/SearchInput';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateMessage';
import { SegmentedControl } from '@/components/ui/Tabs';
import { useToast } from '@/components/ui/Toast';
import { MemberStatusBadge } from '@/features/members';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDate, timestampToDateOnly } from '@/utils/date';
import { getErrorMessage } from '@/utils/errors';
import { pluralize } from '@/utils/text';

import { useLinkMachineUser, useMachineUsers } from '../../hooks/useAttendance';
import type { MachineUser } from '../../schemas/attendance.schema';
import { LinkMemberDialog } from '../LinkMemberDialog';
import { PersonRow } from '../PersonRow';
import styles from './MachineUsersDialog.module.css';

/** A gym has a few hundred people on the machine; one generous page covers a search. */
const PAGE_SIZE = 100;

type Show = 'unlinked' | 'linked';
const SHOW_ITEMS = [
  { value: 'unlinked' as const, label: 'Not linked' },
  { value: 'linked' as const, label: 'Linked' },
];

export interface MachineUsersDialogProps {
  open: boolean;
  onClose: () => void;
  /** Picking for one member: only unlinked people show, and choosing one links them to it. */
  forMember?: { id: number; name: string } | undefined;
}

/** Everyone enrolled on the punch machine, to link each one to the member they are. */
export function MachineUsersDialog({ open, onClose, forMember }: MachineUsersDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title={forMember ? `Find ${forMember.name} on the machine` : 'People on the machine'}
      description={
        forMember
          ? 'Pick the person on the punch machine who is this member.'
          : 'Link each person to the member they are, to see their photo and visits.'
      }
    >
      {open && <Body forMember={forMember} onDone={onClose} />}
    </Dialog>
  );
}

function Body({
  forMember,
  onDone,
}: {
  forMember: MachineUsersDialogProps['forMember'];
  onDone: () => void;
}) {
  const toast = useToast();
  const [show, setShow] = useState<Show>('unlinked');
  const [text, setText] = useState('');
  const query = useDebouncedValue(text.trim());
  const users = useMachineUsers({
    page: 1,
    pageSize: PAGE_SIZE,
    linked: forMember ? false : show === 'linked',
    q: query || undefined,
  });
  const link = useLinkMachineUser();
  const [picking, setPicking] = useState<MachineUser | null>(null);

  const setLink = (user: MachineUser, memberId: number | null) => {
    link.mutate(
      { userId: user.id, memberId },
      {
        onSuccess: () => {
          toast.success(memberId === null ? 'Unlinked' : 'Linked');
          if (forMember) onDone();
        },
        onError: (error) => {
          toast.error(getErrorMessage(error));
        },
      },
    );
  };

  const items = users.data?.items;
  return (
    <div className={styles.body}>
      <div className={styles.toolbar}>
        {!forMember && (
          <SegmentedControl label="Show" value={show} onChange={setShow} items={SHOW_ITEMS} />
        )}
        <SearchInput
          label="Search people on the machine"
          placeholder="Name or machine ID"
          value={text}
          onChange={setText}
          loading={text.trim() !== query || users.isFetching}
        />
      </div>

      {users.isError ? (
        <ErrorState
          compact
          error={users.error}
          onRetry={() => {
            void users.refetch();
          }}
        />
      ) : items === undefined ? (
        <LoadingState compact />
      ) : items.length === 0 ? (
        <EmptyState
          compact
          icon="check"
          title={
            query
              ? 'Nobody matches'
              : show === 'unlinked' || forMember
                ? 'Everyone is linked'
                : 'Nobody linked yet'
          }
        />
      ) : (
        <>
          <ul className={styles.rows}>
            {items.map((user) => (
              <PersonRow
                key={user.id}
                person={user}
                meta={
                  <>
                    <span className="tabular">Machine ID {user.deviceUserId}</span>
                    {user.member ? (
                      <MemberStatusBadge status={user.member.status} />
                    ) : (
                      <Badge tone="neutral">Not linked</Badge>
                    )}
                  </>
                }
                aside={
                  <span className={styles.last}>
                    {user.lastPunchAt
                      ? `Last in ${formatDate(timestampToDateOnly(user.lastPunchAt))}`
                      : 'Never came in'}
                  </span>
                }
                action={
                  user.member ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={link.isPending}
                      onClick={() => {
                        setLink(user, null);
                      }}
                    >
                      Unlink
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon="link"
                      disabled={link.isPending}
                      onClick={() => {
                        if (forMember) setLink(user, forMember.id);
                        else setPicking(user);
                      }}
                    >
                      {forMember ? 'This is them' : 'Link'}
                    </Button>
                  )
                }
              />
            ))}
          </ul>
          {users.data && users.data.meta.total > items.length && (
            <p className={styles.more}>
              Showing {String(items.length)} of{' '}
              {pluralize(users.data.meta.total, 'person', 'people')}. Search to narrow it down.
            </p>
          )}
        </>
      )}

      <LinkMemberDialog
        user={picking}
        onClose={() => {
          setPicking(null);
        }}
      />
    </div>
  );
}
